import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { FACTORY_ADDRESS, ROUTER_ADDRESS, USDT_ADDRESS, PUZO_ADDRESS } from '../config/constants';
import FACTORY_ABI from '../abis/factory.json';
import ROUTER_ABI from '../abis/router.json';
import PAIR_ABI from '../abis/pair.json';

interface PoolData {
  address: string;
  token0: string;
  token1: string;
  reserve0: string;
  reserve1: string;
  totalSupply: string;
}

interface BlockchainData {
  totalPairs: number;
  totalLiquidity: string;
  puzoUsdtPool: PoolData | null;
  volume24h: string;
  loading: boolean;
}

export const useBlockchainData = (provider: ethers.providers.Provider | null) => {
  const [data, setData] = useState<BlockchainData>({
    totalPairs: 0,
    totalLiquidity: '0',
    puzoUsdtPool: null,
    volume24h: '0',
    loading: true
  });

  useEffect(() => {
    if (!provider) {
      setData(prev => ({ ...prev, loading: false }));
      return;
    }

    const fetchData = async () => {
      try {
        const factoryContract = new ethers.Contract(FACTORY_ADDRESS, FACTORY_ABI, provider);

        // 1. ПОЛУЧАЕМ ДАННЫЕ ПУЛА PUZO/USDT
        let puzoUsdtPool = null;
        let totalLiquidity = '0';

        try {
          // Получаем адрес пары PUZO/USDT
          const pairAddress = await factoryContract.getPair(PUZO_ADDRESS, USDT_ADDRESS);
          console.log('PUZO/USDT Pair Address:', pairAddress);

          if (pairAddress && pairAddress !== '0x0000000000000000000000000000000000000000') {
            const pairContract = new ethers.Contract(pairAddress, PAIR_ABI, provider);

            // Получаем данные пула
            const [reserves, totalSupply, token0, token1] = await Promise.all([
              pairContract.getReserves(),
              pairContract.totalSupply(),
              pairContract.token0(),
              pairContract.token1()
            ]);

            console.log('Pool reserves:', reserves);

            // Определяем какой резерв какой токен
            const isPuzoToken0 = token0.toLowerCase() === PUZO_ADDRESS.toLowerCase();
            const puzoReserve = isPuzoToken0 ? reserves[0] : reserves[1];
            const usdtReserve = isPuzoToken0 ? reserves[1] : reserves[0];

            // Форматируем резервы
            const reservePUZO = ethers.utils.formatUnits(puzoReserve, 18);
            const reserveUSDT = ethers.utils.formatUnits(usdtReserve, 18);

            // Расчет ликвидности в USDT
            const liquidityUSD = (parseFloat(reserveUSDT) * 2).toFixed(2);

            puzoUsdtPool = {
              address: pairAddress,
              token0,
              token1,
              reserve0: reservePUZO,
              reserve1: reserveUSDT,
              totalSupply: ethers.utils.formatUnits(totalSupply, 18)
            };

            totalLiquidity = liquidityUSD;
            console.log('💰 Ликвидность пула:', liquidityUSD);
          } else {
            console.log('PUZO/USDT pool not found');
          }
        } catch (error) {
          console.log('PUZO/USDT pool error:', error);
        }

        // 2. СЧИТАЕМ ОБЩЕЕ КОЛИЧЕСТВО ПАР
        let totalPairs = 0;
        try {
          // Для PancakeSwap можно попробовать получить количество пар
          const hasAllPairsLength = FACTORY_ABI.some((item: any) => 
            item.name === 'allPairsLength' && item.type === 'function'
          );

          if (hasAllPairsLength) {
            const pairsLength = await factoryContract.allPairsLength();
            totalPairs = Number(pairsLength);
            console.log('Total pairs found:', totalPairs);
          } else {
            // Если метода нет, считаем что есть хотя бы наш пул
            totalPairs = puzoUsdtPool ? 1 : 0;
          }
        } catch (error) {
          console.log('allPairsLength not available, using counted pairs');
          totalPairs = puzoUsdtPool ? 1 : 0;
        }

        setData({
          totalPairs: totalPairs,
          totalLiquidity: totalLiquidity || '0',
          puzoUsdtPool,
          volume24h: totalLiquidity ? '1,250' : '0', // Временные данные
          loading: false
        });

      } catch (error) {
        console.error('Error fetching blockchain data:', error);
        setData({
          totalPairs: 0,
          totalLiquidity: '0',
          puzoUsdtPool: null,
          volume24h: '0',
          loading: false
        });
      }
    };

    fetchData();

    // Обновляем данные каждые 30 секунд
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [provider]);

  return data;
};
